import { Cesium } from 'app/cesium';

// use the mean radius for our latitude distance calculators (rather than the minimum radius)
// in order to have a consistent amount of error around the globe (versus little at the equator
// and heaps at the poles)
const meanRadius = 6371008.8;

function moveLongitude(longitude: number, distance: number): number {
    const longitudeRadians = Cesium.Math.toRadians(longitude);

    // should be safe enough to use maximumRadius for the longitude distance calculations
    const distanceRadians = distance / Cesium.Ellipsoid.WGS84.maximumRadius;

    return Cesium.Math.toDegrees(longitudeRadians + distanceRadians);
}

function moveLatitude(latitude: number, distance: number): number {
    const latitudeRadians = Cesium.Math.toRadians(latitude);

    // note: use of meanRadius
    const distanceRadians = distance / (meanRadius * Math.cos(latitudeRadians));

    return Cesium.Math.toDegrees(latitudeRadians + distanceRadians);
}

/**
 * The purpose of this class is to use a SingleTileImageryProvider largely as a mock that meets
 * the ImageryProvider interface but behaves consistently- we give some instructions to add an Entity
 * to Cesium that contains our actual image (with rotation) within.
 *
 * This is needed because the SingleTileImageryProvider does not support rotation.
 */
export class SpecialSingleTileImageryProvider extends Cesium.SingleTileImageryProvider {
    _viewer: Cesium.Viewer;
    _entity: Cesium.Entity;

    constructor(
        url: string,
        longitude_of_center: number,
        latitude_of_center: number,
        width_metres: number,
        height_metres: number,
        rotation_degrees: number,
        viewer: Cesium.Viewer,
    ) {
        // options for the small dummy image
        const options = {
            url: 'assets/images/point_small.png',
            rectangle: Cesium.Rectangle.fromDegrees(
                longitude_of_center - 0.000000001,
                latitude_of_center - 0.000000001,
                longitude_of_center + 0.000000001,
                latitude_of_center + 0.000000001,
            ),
        };

        super(options);

        this._viewer = viewer;

        // extract the extents in degrees
        const west = moveLongitude(longitude_of_center, -width_metres / 2);
        const south = moveLatitude(latitude_of_center, -height_metres / 2);
        const east = moveLongitude(longitude_of_center, width_metres / 2);
        const north = moveLatitude(latitude_of_center, height_metres / 2);

        // create an entity that has our desired image and rotated rectangle
        this._entity = this._viewer.entities.add({
            rectangle: {
                coordinates: Cesium.Rectangle.fromDegrees(
                    west, // westernmost longitude in degrees
                    south, // southernmost latitude in degrees
                    east, // easternmost longitude in degrees
                    north, // northernmost latitude in degrees
                ),
                material: url,
                stRotation: Cesium.Math.toRadians(rotation_degrees),
                rotation: Cesium.Math.toRadians(rotation_degrees),
            },
        } as any as Cesium.Entity);
    }

    public specialShow() {
        if (!this._entity) {
            return;
        }

        this._entity.show = true;
    }

    public specialHide() {
        if (!this._entity) {
            return;
        }

        this._entity.show = false;
    }

    public specialDestroy() {
        if (!this._entity) {
            return;
        }

        this._viewer.entities.remove(this._entity);
    }
}
